import React, { useMemo, useState } from 'react'
import { debounce } from 'lodash'
import { SearchLg, FilterLines } from '@untitled-ui/icons-react'
import { MyButton, MyTextField } from '@interstellar-component'
import MyClientFilterModal from '@src/components/MyClientFilterModal'
import { useWorkforce } from '../../../../../../Context'

const DASH = '—'

// Condition operators reused by every field. Mirrors the shape MyFilterModal
// expects from the API (`{ label, value }`); here the config is static because
// the negative-events table is filtered purely on the client (no endpoint).
const STRING_OPS = [
  { label: 'contains', value: 'like' },
  { label: 'equals', value: '=' },
]
const DATE_OPS = [
  { label: 'is', value: '=' },
  { label: 'before', value: '<' },
  { label: 'after', value: '>' },
]

// Field definitions handed to MyFilterModal. String fields point at the
// display value; date fields point at the raw (`*Raw`) value so before/after
// comparisons run on real dates, not the "31 Dec 2024" display string.
const NEGATIVE_EVENTS_FILTERS = [
  { name: 'provider', label: 'Provider', type: 'string', options: STRING_OPS },
  { name: 'type', label: 'Provider type', type: 'string', options: STRING_OPS },
  { name: 'event', label: 'Event', type: 'string', options: STRING_OPS },
  { name: 'dateRaw', label: 'Date', type: 'date', options: DATE_OPS },
  { name: 'expiryRaw', label: 'Expiry', type: 'date', options: DATE_OPS },
  { name: 'lastReferenceRaw', label: 'Last reference', type: 'date', options: DATE_OPS },
]

function formatDate(value) {
  if (!value) return DASH
  const d = new Date(String(value).replace(/\//g, '-'))
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

// Normalise any date-ish value to a "YYYY-MM-DD" key. Comparing these as
// strings is chronological and side-steps timezone drift between the raw API
// date and the ISO value MyCalendar emits (`2024-12-31T00:00:00+07:00`).
function toDayKey(value) {
  if (!value) return null
  const s = String(value).replace(/\//g, '-')
  const m = s.match(/\d{4}-\d{2}-\d{2}/)
  if (m) return m[0]
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return null
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Evaluate a single MyFilterModal condition against one event row.
function matchCondition(event, cond) {
  const op = cond?.condition?.value
  const raw = event[cond?.field]

  if (cond?.filter?.type === 'date') {
    const actual = toDayKey(raw)
    const target = toDayKey(cond?.value)
    if (!actual || !target) return false
    if (op === '<') return actual < target
    if (op === '>') return actual > target
    return actual === target // 'is'
  }

  const fieldValue = String(raw ?? '').toLowerCase()
  const condValue = String(cond?.value ?? '').toLowerCase()
  if (op === '=') return fieldValue === condValue
  return fieldValue.includes(condValue) // 'like'
}

export default function NegativeEventsSection() {
  const { workforceDetailReport } = useWorkforce()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState([])

  // Built by core/service/credit-report/builder.js#deriveNegativeEvents from
  // CLIK worst-status + bad-credit annotations. These are credit delinquency
  // events and nothing else — Everest has no court/legal source (the Mahkamah
  // Agung one was cancelled on 28 Aug 2026), so never relabel these as court
  // records. Empty array = no negative events; render an empty state, not demo
  // rows.
  const allEvents = useMemo(() => {
    const apiList = workforceDetailReport?.negative_events ?? []
    return apiList.map((e) => ({
      provider: e.provider ?? DASH,
      type: e.provider_type ?? '',
      event: e.description ?? DASH,
      date: formatDate(e.date),
      expiry: formatDate(e.expiry_date),
      lastReference: formatDate(e.last_reference ?? e.date),
      // Raw values kept for date filtering / comparisons.
      dateRaw: e.date ?? null,
      expiryRaw: e.expiry_date ?? null,
      lastReferenceRaw: e.last_reference ?? e.date ?? null,
    }))
  }, [workforceDetailReport?.negative_events])

  const searched = useMemo(() => {
    if (!search) return allEvents
    const q = search.toLowerCase()
    return allEvents.filter(
      (e) =>
        e.provider.toLowerCase().includes(q) ||
        e.event.toLowerCase().includes(q) ||
        (e.type ?? '').toLowerCase().includes(q)
    )
  }, [allEvents, search])

  // Apply the MyFilterModal conditions on top of the search result. The
  // combinator (and/or) is global in MyFilterModal — every condition shares the
  // operator set on the second row — so AND => every, OR => some.
  const events = useMemo(() => {
    if (!filters.length) return searched
    const combinator = filters.find((c) => c?.operator?.value)?.operator?.value || 'and'
    return searched.filter((e) =>
      combinator === 'or'
        ? filters.some((c) => matchCondition(e, c))
        : filters.every((c) => matchCondition(e, c))
    )
  }, [searched, filters])

  const onSearchChange = useMemo(
    () => debounce((e) => setSearch(e.target.value ?? ''), 500),
    []
  )

  const hasActiveFilter = Boolean(search) || filters.length > 0

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex flex-col gap-1">
        <h3 className="text-xl font-bold text-gray-900">Negative events</h3>
        <p className="text-sm text-gray-500">
          Breakdown of active and historical credit facilities associated with this individual.
        </p>
      </div>

      <div className="flex flex-col border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
        {/* Toolbar */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <div className="w-full max-w-sm">
            <MyTextField
              placeholder="Search for event"
              startAdornment={
                <SearchLg className="size-5 text-gray-light/600" stroke="currentColor" />
              }
              focusColor="#14378E"
              onChangeForm={onSearchChange}
            />
          </div>
          <MyClientFilterModal
            id="filter-negative-events"
            currentFilters={NEGATIVE_EVENTS_FILTERS}
            onChange={(filter) =>
              setFilters(Array.isArray(filter) ? filter : Object.values(filter || {}))
            }
            target={(open, handleClick) => (
              <MyButton
                color="gray"
                variant="tertiary"
                size="md"
                customClassname="gap-2 text-gray-700"
                onClick={handleClick}
              >
                <FilterLines className="h-5 w-5 text-gray-700" />
                <span className="text-gray-700">Filters</span>
              </MyButton>
            )}
          />
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-[#f9fafb] text-gray-500 font-medium border-b border-gray-200">
              <tr>
                <th className="px-6 py-3">Provider & type</th>
                <th className="px-6 py-3">Event</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Expiry</th>
                <th className="px-6 py-3">Last reference</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {events.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <p className="text-sm font-medium text-gray-900">No negative events</p>
                      <p className="text-xs text-gray-500 max-w-sm">
                        {hasActiveFilter
                          ? 'No records match your search or filters. Try adjusting them.'
                          : 'CLIK reported no worst-status events or bad-credit annotations for this individual.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                events.map((ev, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{ev.provider}</span>
                        <span className="text-xs text-gray-500">{ev.type}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">{ev.event}</td>
                    <td className="px-6 py-4">{ev.date}</td>
                    <td className="px-6 py-4">{ev.expiry}</td>
                    <td className="px-6 py-4">{ev.lastReference}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer count */}
        <div className="flex items-center px-6 py-4 border-t border-gray-200 bg-white">
          <span className="text-sm text-gray-600 font-medium">
            {events.length} event{events.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>
    </div>
  )
}
